import React, { useEffect } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import toast, { Toaster, ToastBar } from "react-hot-toast";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import store from "./features/shared/redux/store";
import { Box, CircularProgress, CssBaseline, IconButton, ThemeProvider } from "@mui/material";
import { AppTheme } from "./features/shared/styles/Theme";
import {
  AccountRoute,
  PlatformMaintenanceRoute,
  AnalyticsDataReferenceRoute,
  RevenueAnalyticsDataReferenceRoute,
  TrackingMaintenanceRoute,
  DashboardRoute,
  DocDisplayRoute,
  DocDisplayImplantitisRoute,
  OrdersCreateRoute,
  OrdersEditRoute,
  OrdersRoute,
  KitOrdersRoute,
  PurchaseHistoryRoute,
  NewReportOrdersRoute,
  PrefilterOrdersRoute,
  OrganizationsCreateRoute,
  OrganizationsEditRoute,
  OrganizationsRoute,
  OrganizationBillingEditRoute,
  OrgBillingReportsRoute,
  OrgActivityReportsRoute,
  ResetPasswordChangeRoute,
  ResetPasswordRoute,
  SignInRoute,
  SignInSimplyPerioRoute,
  SignInSimplyImplantitisRoute,
  SignOutRoute,
  SignOutSimplyPerioRoute,
  SignOutSimplyImplantitisRoute,
  AddStripePaymentMethodRoute,
  AddStripeReturnRoute,
  EnterpriseStripeSetupRoute,
  EnterpriseStripeReturnRoute,
  SuppliesRoute,
  KitsRoute,
  KitOrderShipToClinicRoute,
  ShipToPatientRoute,
  PatientPaymentRoute,
  ClinicSubmissionRoute,
  SupportRoute,
  UserGroupsCreateRoute,
  UserGroupsEditRoute,
  UserGroupsRoute,
  UsersCreateRoute,
  UsersEditRoute,
  UsersRoute,
} from "./Routes";
import { APP_TITLE, ROUTES } from "./features/shared/constants";
import "simplebar-react/dist/simplebar.min.css";
import { loadApp, selectIsAppBusy } from "./features/shared/redux/slice";

export const App: React.FC<{}> = () => {
  return (
    <ThemeProvider theme={AppTheme}>
      <Toaster
        position="top-right"
        containerStyle={{ top: 80, right: 20, zIndex: 2000 }}
        toastOptions={{
          duration: 4500,
          style: {
            maxWidth: "440px",
            padding: "12px 16px",
            borderRadius: "10px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 20px rgba(13,27,42,0.10), 0 1px 3px rgba(13,27,42,0.06)",
            backgroundColor: "#FFFFFF",
            color: "#0D1B2A",
            fontWeight: 500,
            fontSize: "13.5px",
          },
          success: {
            iconTheme: { primary: "#059669", secondary: "#FFFFFF" },
            style: { borderLeft: "3px solid #059669" },
          },
          error: {
            iconTheme: { primary: "#DC2626", secondary: "#FFFFFF" },
            style: { borderLeft: "3px solid #DC2626" },
          },
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                {t.type !== "loading" && (
                  <IconButton
                    size="small"
                    onClick={() => toast.dismiss(t.id)}
                    sx={{ p: "2px", ml: "2px", color: "#9CA3AF", "&:hover": { color: "#374151", backgroundColor: "#F1F5F9" } }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: "15px" }} />
                  </IconButton>
                )}
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
      <CssBaseline />
      <Helmet>
        <title>{APP_TITLE}</title>
      </Helmet>
      <Provider store={store}>
        <AppRoutes />
      </Provider>
    </ThemeProvider>
  );
};

export const AppRoutes: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const appIsLoading = useSelector(selectIsAppBusy);

  useEffect(() => {
    dispatch(loadApp());
  }, [dispatch]);

  if (appIsLoading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#F0F4F8' }}>
      <CircularProgress size={36} thickness={4} sx={{ color: '#0891B2' }} />
    </Box>
  );

  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path={ROUTES.SignIn} element={<SignInRoute />} />
          <Route path={ROUTES.SignInSimplyPerio} element={<SignInSimplyPerioRoute />} />
          <Route path={ROUTES.SignInSimplyImplantitis} element={<SignInSimplyImplantitisRoute />} />
          <Route path={ROUTES.ResetPassword} element={<ResetPasswordRoute />} />
          <Route path={ROUTES.ResetPasswordChange} element={<ResetPasswordChangeRoute />} />
          <Route path={ROUTES.Dashboard} element={<DashboardRoute />} />
          <Route path={ROUTES.DocDisplay} element={<DocDisplayRoute />} />
          <Route path={ROUTES.DocDisplayImplantitis} element={<DocDisplayImplantitisRoute />} />
          <Route path={ROUTES.Orders} element={<OrdersRoute />} />
          <Route path={ROUTES.KitOrders} element={<KitOrdersRoute />} />
          <Route path={ROUTES.PurchaseHistory} element={<PurchaseHistoryRoute />} />
          <Route path={ROUTES.NewReportOrders} element={<NewReportOrdersRoute />} />
          <Route path={ROUTES.PrefilterOrders} element={<PrefilterOrdersRoute />} />
          <Route path={ROUTES.CreateOrder} element={<OrdersCreateRoute />} />
          <Route path={ROUTES.EditOrder} element={<OrdersEditRoute />} />
          <Route path={ROUTES.UserGroups} element={<UserGroupsRoute />} />
          <Route path={ROUTES.CreateUserGroup} element={<UserGroupsCreateRoute />} />
          <Route path={ROUTES.EditUserGroup} element={<UserGroupsEditRoute />} />
          <Route path={ROUTES.Users} element={<UsersRoute />} />
          <Route path={ROUTES.CreateUser} element={<UsersCreateRoute />} />
          <Route path={ROUTES.EditUser} element={<UsersEditRoute />} />
          <Route path={ROUTES.Organizations} element={<OrganizationsRoute />} />
          <Route path={ROUTES.CreateOrganization} element={<OrganizationsCreateRoute />} />
          <Route path={ROUTES.EditOrganization} element={<OrganizationsEditRoute />} />
          <Route path={ROUTES.CreateOrganizationUser} element={<UsersCreateRoute />} />
          <Route path={ROUTES.EditOrganizationUser} element={<UsersEditRoute />} />
          <Route path={ROUTES.OrgBillingReports} element={<OrgBillingReportsRoute />} />
          <Route path={ROUTES.OrgActivityReports} element={<OrgActivityReportsRoute />} />
          <Route path={ROUTES.EditOrganizationBilling} element={<OrganizationBillingEditRoute />} />
          <Route path={ROUTES.AddStripePaymentMethod} element={<AddStripePaymentMethodRoute />} />
          <Route path={ROUTES.StripeReturnPage} element={<AddStripeReturnRoute />} />
          <Route path={ROUTES.EnterpriseStripeSetup} element={<EnterpriseStripeSetupRoute />} />
          <Route path={ROUTES.EnterpriseStripeReturn} element={<EnterpriseStripeReturnRoute />} />
          <Route path={ROUTES.Supplies} element={<SuppliesRoute />} />
          <Route path={ROUTES.Kits} element={<KitsRoute />} />
          <Route path={ROUTES.KitOrderShipToClinic} element={<KitOrderShipToClinicRoute />} />
          <Route path={ROUTES.ShipToPatient} element={<ShipToPatientRoute />} />
          <Route path={ROUTES.PayLink} element={<PatientPaymentRoute />} />
          <Route path={ROUTES.ClinicSubmission} element={<ClinicSubmissionRoute />} />
          <Route path={ROUTES.Support} element={<SupportRoute />} />
          <Route path={ROUTES.Account} element={<AccountRoute />} />
          <Route path={ROUTES.PlatformMaintenance} element={<PlatformMaintenanceRoute />} />
          <Route path={ROUTES.AnalyticsDataReference} element={<AnalyticsDataReferenceRoute />} />
          <Route path={ROUTES.RevenueAnalyticsDataReference} element={<RevenueAnalyticsDataReferenceRoute />} />
          <Route path={ROUTES.TrackingMaintenance} element={<TrackingMaintenanceRoute />} />
          <Route path={ROUTES.SignOut} element={<SignOutRoute />} />
          <Route path="*" element={<Navigate to={ROUTES.SignIn} />} />
        </Routes>
      </BrowserRouter>
    </>
  );
};
